import React, { useEffect, useState } from 'react';
import { Card, Typography, Box, Grid } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { KeyUri } from '../key';

/**
 * Pending / Approved / Draft (and optionally Total) cards for a vendor.
 *
 * Props
 *  - properties:  pass the vendor's properties if the parent already has them
 *                 (Dashboard does). If omitted, the component fetches them itself.
 *  - refreshKey:  change this value to re-fetch (e.g. after saving a draft).
 *                 Only used when `properties` is not passed.
 *  - showTotal:   also show a "Total Properties" card.
 *  - compact:     smaller cards, for use above a form.
 */
export function PropertyStatusCards({ properties, refreshKey = 0, showTotal = false, compact = false }) {
  const [fetched, setFetched] = useState([]);

  useEffect(() => {
    if (properties) return; // parent supplies the data

    let cancelled = false;
    const load = async () => {
      const token = localStorage.getItem('vendorToken');
      if (!token) return;
      try {
        const res = await axios.get(`${KeyUri.BACKEND_URI}/get-properties`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setFetched(res.data.data || []);
      } catch (err) {
        console.error('Error fetching property counts:', err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [properties, refreshKey]);

  const list = properties || fetched;
  const count = (status) => list.filter((p) => p.status === status).length;

  const cards = [
    ...(showTotal
      ? [{ label: 'Total Properties', value: list.length, icon: BusinessIcon, color: '#2563EB', bgColor: '#EFF6FF' }]
      : []),
    { label: 'Pending Review', value: count('pending'), icon: AccessTimeIcon, color: '#CA8A04', bgColor: '#FEF3C7' },
    { label: 'Approved', value: count('approved'), icon: CheckCircleIcon, color: '#16A34A', bgColor: '#DCFCE7' },
    { label: 'Draft', value: count('draft'), icon: EditIcon, color: '#4B5563', bgColor: '#F3F4F6' },
  ];

  const md = 12 / cards.length;

  return (
    <Grid container spacing={compact ? 2 : 3}>
      {cards.map(({ label, value, icon: Icon, color, bgColor }) => (
        <Grid item xs={12} sm={compact ? 4 : 6} md={md} key={label}>
          <Card sx={{ p: compact ? 2 : 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {label}
                </Typography>
                <Typography variant={compact ? 'h4' : 'h3'} color="text.primary" fontWeight={700}>
                  {value}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: bgColor,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: compact ? 40 : 48,
                  height: compact ? 40 : 48,
                }}
              >
                <Icon sx={{ color, fontSize: compact ? 22 : 28 }} />
              </Box>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export default PropertyStatusCards;